import React, { memo } from 'react';
import { styled as styled2 } from '@compiled/react';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import SubtaskIcon from '@atlaskit/icon/glyph/subtask';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { borderRadius as stBorderRadius } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import type { SubtaskMetadata } from '../../../../../../common/types';
// eslint-disable-next-line jira/import/no-parent-imports
import messages from '../../../../../board/column/card/card-content/subtasks/subtasks-expand-collapse/messages';

type Props = {
	subtaskMetadata?: SubtaskMetadata;
	withSubtasks?: boolean;
};

export const CardSubtasks = memo<Props>(({ subtaskMetadata, withSubtasks }: Props) => {
	const { formatMessage } = useIntl();

	if (!subtaskMetadata?.total) {
		return null;
	}

	const tooltipContent = formatMessage(
		withSubtasks ? messages.hideSubtasksTooltip : messages.showSubtasksTooltip,
	);

	return (
		<ToolTip content={tooltipContent}>
			<SubtaskContainer>
				<SubtaskIconWrapper>
					<SubtaskIcon size="small" label="" />
				</SubtaskIconWrapper>
				<SubtaskDoneArea>
					{subtaskMetadata.done}/{subtaskMetadata.total}
				</SubtaskDoneArea>
				<SubtaskExpandCollapse
					withSubtasks={!!withSubtasks}
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="work-management-board.ui.board.column.card.card-content.card-subtasks.subtask-expand-collapse"
				>
					<ChevronRightIcon label={tooltipContent} />
				</SubtaskExpandCollapse>
			</SubtaskContainer>
		</ToolTip>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubtaskIconWrapper = styled2.span({
	verticalAlign: 'middle',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubtaskContainer = styled2.div({
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.background.neutral', colors.N20A),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N100),
	padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${stBorderRadius}px`,
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubtaskDoneArea = styled2.span({
	marginLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SubtaskExpandCollapse = styled2.span<{ withSubtasks: boolean }>({
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '-10px',
	marginTop: token('space.negative.025', '-2px'),
	display: 'inline-block',
	transition: 'transform 0.1s ease-out',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	transform: ({ withSubtasks }) => (withSubtasks ? 'rotate(90deg)' : 'rotate(0)'),
});
